/**
 * Sawyer 10 customer-booking-verify-policy (C5 C6) — the admin client for
 * Settings → Operations → Care details at booking
 * (`GET/PUT /api/v2/admin/tenant/booking-care`).
 *
 * The READ is a raw fetch on purpose (refuter C4, the never-sign-out probe
 * pattern of `probeOwnerSettingsAvailable`): frontends auto-deploy before the
 * server, and an older server answers this path with 403 `route_not_mapped`.
 * That, a 404, or a role 403 is `absent` — the card renders nothing. Only a
 * 5xx or a network failure is a real error with Retry.
 */
import { api, humanizeApiError, readSession, type ApiResponse } from './api.js';

export const BOOKING_CARE_PATH = '/api/v2/admin/tenant/booking-care';

export type BookingCareKey =
  | 'careVerificationServices'
  | 'medicationPolicyText'
  | 'bannedMedications'
  | 'lockboxMedications';

export interface TenantBookingCareSettings {
  bookingCare: {
    careVerificationServices: string[];
    medicationPolicyText: string | null;
    bannedMedications: string[];
    lockboxMedications: string[];
  };
  source: Record<BookingCareKey, 'configured' | 'default'>;
  limits: { maxPolicyChars: number; maxListEntries: number; maxEntryChars: number; maxServices: number };
  lastChange: { at: string; byName: string; changedKeys: BookingCareKey[] } | null;
}

export type TenantBookingCarePatch = Partial<{
  careVerificationServices: string[];
  medicationPolicyText: string | null;
  bannedMedications: string[];
  lockboxMedications: string[];
}>;

export type BookingCareLoad =
  | { kind: 'ok'; data: TenantBookingCareSettings }
  /** Older server (route_not_mapped), no config row, or a role without read: render nothing. */
  | { kind: 'absent' }
  | { kind: 'error'; message: string };

export const BOOKING_CARE_LOAD_ERROR = "We couldn't load these settings.";

export async function loadBookingCareSettings(apiUrl: string): Promise<BookingCareLoad> {
  const { token } = readSession();
  let res: Response;
  try {
    res = await fetch(`${apiUrl}${BOOKING_CARE_PATH}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    return { kind: 'error', message: BOOKING_CARE_LOAD_ERROR };
  }
  if (res.status === 403 || res.status === 404) return { kind: 'absent' };
  if (!res.ok) return { kind: 'error', message: BOOKING_CARE_LOAD_ERROR };
  try {
    const data = (await res.json()) as TenantBookingCareSettings;
    if (!data || typeof data !== 'object' || !data.bookingCare) {
      return { kind: 'error', message: BOOKING_CARE_LOAD_ERROR };
    }
    return { kind: 'ok', data };
  } catch {
    return { kind: 'error', message: BOOKING_CARE_LOAD_ERROR };
  }
}

export type BookingCareSaveResponse = TenantBookingCareSettings & {
  changedKeys: BookingCareKey[];
  audited: boolean;
};

export function saveBookingCareSettings(
  apiUrl: string,
  patch: TenantBookingCarePatch,
): Promise<ApiResponse<BookingCareSaveResponse>> {
  return api.put<BookingCareSaveResponse>(apiUrl, BOOKING_CARE_PATH, patch);
}

/** The field-named messages of a 400 (`details: [{ field, message }]`), keyed by field. */
export function fieldErrorsOf(details: unknown): Partial<Record<BookingCareKey, string>> {
  const out: Partial<Record<BookingCareKey, string>> = {};
  if (!Array.isArray(details)) return out;
  for (const d of details) {
    if (!d || typeof d !== 'object') continue;
    const field = String((d as { field?: unknown }).field ?? '').split('.')[0] as BookingCareKey;
    const message = (d as { message?: unknown }).message;
    if (typeof message === 'string' && !out[field]) out[field] = humanizeApiError(message);
  }
  return out;
}

/** One entry per line: trimmed, blank lines dropped, case-insensitive duplicates collapsed (first kept). */
export function linesToList(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split('\n')) {
    const entry = raw.trim();
    if (entry.length === 0) continue;
    const key = entry.toLocaleLowerCase('en-US');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}
